import {
  Children,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import { cn } from "@/lib/utils";
import type { RagCitation } from "@/apps/rag/types";
import {
  hasOpenableCitationSource,
  openCitationDocument,
  type OnOpenCitationDocument,
} from "@/apps/rag/lib/open-citation-document";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PROSE_CLASS =
  "prose max-w-none text-[13px] leading-6 prose-p:my-1 prose-p:text-[13px] prose-headings:my-2 prose-headings:font-semibold prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-h4:text-sm prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:text-[13px] prose-strong:text-[#0b1120] prose-code:text-[#1d4ed8] prose-code:bg-[#eef4ff] prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-[#f7f8fc] prose-pre:border prose-pre:border-[rgba(15,23,42,0.09)] dark:prose-invert dark:prose-strong:text-white dark:prose-code:text-blue-300 dark:prose-code:bg-blue-900/30 dark:prose-pre:bg-slate-800 dark:prose-pre:border-white/10";

const CITATION_MARKER_REGEX = /\[(\d+)\]|【(\d+)】/g;
const ORPHAN_CITATION_MARKER_PATTERN = /(?:\[\d+\]|【\d+】)/;
/** Section anchors emitted by RAG models alongside numeric citations, e.g. [§3(c)]. */
const SECTION_CITATION_MARKER_REGEX = /\s*\[§[^\]]*\]/g;
const EXCERPT_PREVIEW_MAX = 140;

function parseCitationIndex(match: RegExpMatchArray): number {
  return Number.parseInt(match[1] ?? match[2] ?? "", 10);
}

function truncateExcerpt(text: string, max = EXCERPT_PREVIEW_MAX): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trimEnd()}…`;
}

function formatCitationMeta(
  citation: Pick<RagCitation, "page" | "section">
): string | null {
  const parts: string[] = [];
  if (citation.page) parts.push(`Page ${citation.page}`);
  if (citation.section) parts.push(citation.section);
  return parts.length > 0 ? parts.join(" · ") : null;
}

interface AssistantMarkdownContentProps {
  content: string;
  citations?: RagCitation[];
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
  className?: string;
  /** Skip citation placement transforms while tokens are still arriving. */
  isStreaming?: boolean;
}

interface CitationRenderContext {
  citationByIndex: Map<number, RagCitation>;
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
}

function InlineCitation({
  citation,
  activeCitationId,
  onCitationClick,
  threadId,
  onOpenDocument,
}: {
  citation: RagCitation;
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
}) {
  const meta = formatCitationMeta(citation);
  const excerpt = citation.excerpt ? truncateExcerpt(citation.excerpt) : null;
  const canOpen = hasOpenableCitationSource(citation);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  async function handleOpenDocument(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    setOpening(true);
    setOpenError(null);

    try {
      await openCitationDocument({ citation, threadId, onOpenDocument });
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : "Failed to open document");
    } finally {
      setOpening(false);
    }
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onCitationClick?.(citation)}
            className={cn(
              "not-prose mx-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full align-super text-[10px] font-bold leading-none transition-colors",
              activeCitationId === citation.id
                ? "bg-primary text-white ring-2 ring-[rgba(147,51,245,0.3)] dark:bg-violet-500 dark:ring-violet-300/50"
                : "bg-[var(--citation-bg)] text-[#7c3aed] border border-[var(--citation-border)] hover:bg-[rgba(147,51,245,0.15)] dark:bg-violet-600 dark:text-white dark:border-transparent dark:hover:bg-violet-500"
            )}
          >
            {citation.index}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] space-y-1.5 px-3 py-2.5"
        >
          <div className="flex items-start gap-2">
            <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-violet-600 text-[9px] font-bold leading-none text-white">
              {citation.index}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold leading-tight">
                {citation.documentTitle}
              </p>
              {meta ? (
                <p className="mt-0.5 text-[10px] text-[#576274] dark:text-slate-400">
                  {meta}
                </p>
              ) : null}
            </div>
            <span className="shrink-0 text-[10px] font-medium text-[#576274] dark:text-slate-400">
              {citation.relevance}%
            </span>
          </div>
          {excerpt ? (
            <p className="text-[11px] leading-4 text-[#576274] dark:text-slate-300">
              {excerpt}
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="w-full"
            disabled={opening || !canOpen}
            onClick={(event) => void handleOpenDocument(event)}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {opening ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ExternalLink className="h-3.5 w-3.5" />
            )}
            Open full document
          </Button>
          {openError ? (
            <p className="text-center text-[10px] text-red-400">{openError}</p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Remove redundant section anchors that follow numeric citation markers. */
export function stripSectionCitationMarkers(content: string): string {
  return content.replace(SECTION_CITATION_MARKER_REGEX, "");
}

/** Move orphan citation-only lines onto the preceding line. */
export function normalizeCitationInlinePlacement(content: string): string {
  return content.replace(
    new RegExp(
      `\\n+\\s*((?:${ORPHAN_CITATION_MARKER_PATTERN.source}\\s*)+)\\s*(?=\\n|$)`,
      "g",
    ),
    " $1",
  );
}

export function contentHasKnownCitations(
  content: string,
  citationByIndex: Map<number, RagCitation>
): boolean {
  if (citationByIndex.size === 0) return false;

  for (const match of content.matchAll(CITATION_MARKER_REGEX)) {
    const index = parseCitationIndex(match);
    if (citationByIndex.has(index)) return true;
  }

  return false;
}

export function getCitedCitations(
  content: string,
  citations: RagCitation[] | undefined
): RagCitation[] {
  if (!citations?.length) return [];

  const citationByIndex = new Map<number, RagCitation>();
  for (const citation of citations) {
    citationByIndex.set(citation.index, citation);
  }

  const citedIndices = new Set<number>();
  for (const match of content.matchAll(CITATION_MARKER_REGEX)) {
    const index = parseCitationIndex(match);
    if (citationByIndex.has(index)) {
      citedIndices.add(index);
    }
  }

  return [...citedIndices]
    .sort((a, b) => a - b)
    .map((index) => citationByIndex.get(index)!);
}

export type TextCitationPart =
  | { kind: "text"; value: string }
  | { kind: "citation"; citation: RagCitation };

export function splitTextWithCitationMarkers(
  text: string,
  citationByIndex: Map<number, RagCitation>
): TextCitationPart[] {
  const parts: TextCitationPart[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(CITATION_MARKER_REGEX)) {
    const index = parseCitationIndex(match);
    const citation = citationByIndex.get(index);
    if (!citation) continue;

    const offset = match.index ?? 0;

    if (offset > lastIndex) {
      parts.push({ kind: "text", value: text.slice(lastIndex, offset) });
    }

    parts.push({ kind: "citation", citation });

    lastIndex = offset + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ kind: "text", value: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ kind: "text", value: text }];
}

export function renderInlineWithCitations(
  text: string,
  ctx: CitationRenderContext,
  keyPrefix: string
): ReactNode[] {
  return splitTextWithCitationMarkers(text, ctx.citationByIndex).map((part, partIndex) => {
    if (part.kind === "text") return part.value;

    return (
      <InlineCitation
        key={`${keyPrefix}-cite-${partIndex}`}
        citation={part.citation}
        activeCitationId={ctx.activeCitationId}
        onCitationClick={ctx.onCitationClick}
        threadId={ctx.threadId}
        onOpenDocument={ctx.onOpenDocument}
      />
    );
  });
}

export function processCitationChildren(
  children: ReactNode,
  ctx: CitationRenderContext,
  keyPrefix: string
): ReactNode {
  return Children.toArray(children).flatMap((child, index) => {
    const childKey = `${keyPrefix}-${index}`;

    if (typeof child === "string") {
      return renderInlineWithCitations(child, ctx, childKey);
    }

    if (typeof child === "number") {
      return child;
    }

    if (isValidElement(child)) {
      if (child.props.children != null) {
        return cloneElement(child, {
          ...child.props,
          key: child.key ?? childKey,
          children: processCitationChildren(child.props.children, ctx, childKey),
        });
      }
      return child;
    }

    return child;
  });
}

function withCitationChildren(
  children: ReactNode,
  ctx: CitationRenderContext | null,
  keyPrefix: string
): ReactNode {
  if (!ctx) return children;
  return processCitationChildren(children, ctx, keyPrefix);
}

function createMarkdownComponents(ctx: CitationRenderContext | null): Components {
  return {
    table: ({ ...props }) => (
      <div className="my-2 w-full overflow-x-auto">
        <table className="min-w-[560px] border-collapse text-left text-xs" {...props} />
      </div>
    ),
    th: ({ children, ...props }) => (
      <th
        className="border-b border-[var(--lm-border-subtle)] dark:border-white/15 px-2 py-2 align-top font-semibold text-[var(--lm-foreground-strong)] dark:text-gray-200"
        {...props}
      >
        {withCitationChildren(children, ctx, "th")}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="border-b border-[var(--lm-border-subtle)] dark:border-white/10 px-2 py-2 align-top text-[var(--lm-foreground)] dark:text-gray-300" {...props}>
        {withCitationChildren(children, ctx, "td")}
      </td>
    ),
    a: ({ href, children, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-300 underline decoration-blue-300/50 hover:text-blue-200"
        {...props}
      >
        {withCitationChildren(children, ctx, "a")}
      </a>
    ),
    p: ({ children, ...props }) => (
      <p {...props}>{withCitationChildren(children, ctx, "p")}</p>
    ),
    li: ({ children, ...props }) => (
      <li {...props}>{withCitationChildren(children, ctx, "li")}</li>
    ),
    h1: ({ children, ...props }) => (
      <h1 {...props}>{withCitationChildren(children, ctx, "h1")}</h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 {...props}>{withCitationChildren(children, ctx, "h2")}</h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 {...props}>{withCitationChildren(children, ctx, "h3")}</h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 {...props}>{withCitationChildren(children, ctx, "h4")}</h4>
    ),
    h5: ({ children, ...props }) => (
      <h5 {...props}>{withCitationChildren(children, ctx, "h5")}</h5>
    ),
    h6: ({ children, ...props }) => (
      <h6 {...props}>{withCitationChildren(children, ctx, "h6")}</h6>
    ),
    blockquote: ({ children, ...props }) => (
      <blockquote {...props}>{withCitationChildren(children, ctx, "blockquote")}</blockquote>
    ),
    strong: ({ children, ...props }) => (
      <strong {...props}>{withCitationChildren(children, ctx, "strong")}</strong>
    ),
    em: ({ children, ...props }) => (
      <em {...props}>{withCitationChildren(children, ctx, "em")}</em>
    ),
  };
}

export function AssistantMarkdownContent({
  content,
  citations,
  activeCitationId,
  onCitationClick,
  threadId,
  onOpenDocument,
  className,
  isStreaming = false,
}: AssistantMarkdownContentProps) {
  const citationByIndex = useMemo(() => {
    const map = new Map<number, RagCitation>();
    for (const citation of citations ?? []) {
      map.set(citation.index, citation);
    }
    return map;
  }, [citations]);

  const citationContext = useMemo<CitationRenderContext | null>(() => {
    if (isStreaming) return null;
    if (!contentHasKnownCitations(content, citationByIndex)) return null;
    return {
      citationByIndex,
      activeCitationId,
      onCitationClick,
      threadId,
      onOpenDocument,
    };
  }, [
    content,
    citationByIndex,
    activeCitationId,
    onCitationClick,
    threadId,
    onOpenDocument,
    isStreaming,
  ]);

  const markdownContent = useMemo(() => {
    let result = content;
    if (!isStreaming && citationContext) {
      result = normalizeCitationInlinePlacement(result);
    }
    return stripSectionCitationMarkers(result);
  }, [content, citationContext, isStreaming]);

  const components = useMemo(
    () => createMarkdownComponents(citationContext),
    [citationContext]
  );

  return (
    <div className={cn(PROSE_CLASS, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
}
